import { LocalDate } from "@js-joda/core";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  getBusinessDate,
  getBusinessDayOfWeek,
  getOperatingHoursList,
  getWeekBounds,
  jsDayToBucketIndex,
  parseTimestamp,
  walkTimeReportSlots,
} from "./analytics.operatingHours";
import { type CustomCost, getCustomCostsTotal } from "./analytics.customCosts";
import { type CustomSale } from "./analytics.customSales";
import { type CompanyTipTotal } from "./analytics.companyTips";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChartBucket {
  label: string;
  /** Sales fetched from the API — excludes custom sales, which get their own field. */
  sales: number;
  /** Manually-entered sales, kept separate so the chart can show them distinctly. */
  customSales: number;
  tips: number;
  personnelCost: number;
  /** Manually-entered "Other costs" (period + date), kept separate from
   * personnelCost so the chart can show them as a distinct series. */
  otherCosts: number;
  result: number;
}

export interface BuildChartDataParams {
  orders: Order[];
  timeReports: TimeReportDto[];
  period: AnalyticsPeriod;
  selectedDate: LocalDate;
  operatingHours: OperatingHours;
  /** Translated short weekday labels (Mon–Sun), used as bucket labels in week view */
  weekdayLabels: string[];
  /** Translated short month labels (Jan–Dec), used as bucket labels in year view */
  monthLabels: string[];
  /** Actual total from the API — chart buckets are scaled so they sum to this value exactly */
  totalPersonnelCost: number | null;
  /** Custom costs — period costs distributed evenly; date costs attributed to their specific bucket */
  customCosts: CustomCost[];
  /** Custom sales — each attributed to its specific date bucket's sales figure */
  customSales: CustomSale[];
  /** Tips recorded on the Tips page — folded into each date bucket's tips */
  companyTips: CompanyTipTotal[];
}

// ─── Bucket builders ──────────────────────────────────────────────────────────

const buildDayBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): ChartBucket[] => {
  // joda DayOfWeek.value(): 1=Mon…7=Sun → bucket index 0–6
  const bucketIdx = selectedDate.dayOfWeek().value() - 1;
  const hoursList = getOperatingHoursList(operatingHours[bucketIdx]);
  const hourToIndex = new Map(hoursList.map((h, i) => [h, i]));

  const buckets: ChartBucket[] = hoursList.map((hour) => ({
    label: `${String(hour).padStart(2, "0")}:00`,
    sales: 0,
    customSales: 0,
    tips: 0,
    personnelCost: 0,
    otherCosts: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const idx = hourToIndex.get(d.getHours());
    if (idx === undefined) continue;
    buckets[idx].sales += order.amount;
    buckets[idx].tips += order.tip;
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const idx = hourToIndex.get(slotStart.getHours());
      if (idx !== undefined) {
        buckets[idx].personnelCost += report.hourlyRate * fractionHours;
      }
    });
  }

  return buckets;
};

const buildWeekBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  selectedDate: LocalDate,
  weekdayLabels: string[],
  operatingHours: OperatingHours,
): ChartBucket[] => {
  const buckets: ChartBucket[] = Array.from({ length: 7 }, (_, i) => ({
    label: weekdayLabels[i],
    sales: 0,
    customSales: 0,
    tips: 0,
    personnelCost: 0,
    otherCosts: 0,
    result: 0,
  }));

  // Day-of-week alone is not enough to place a row: the fetch range is padded
  // by a day for overnight windows, so a timestamp from the Monday *after*
  // this week would otherwise land in this week's Monday bucket. Verify the
  // business date is inside the selected week, as month/year builders do.
  const { weekStart, weekEnd } = getWeekBounds(selectedDate);
  const bucketIndexFor = (ts: Date): number | null => {
    const bizDate = getBusinessDate(ts, operatingHours);
    if (!bizDate || bizDate.isBefore(weekStart) || bizDate.isAfter(weekEnd)) {
      return null;
    }
    const businessJsDay = getBusinessDayOfWeek(ts, operatingHours);
    return businessJsDay === null ? null : jsDayToBucketIndex(businessJsDay);
  };

  for (const order of orders) {
    const idx = bucketIndexFor(parseTimestamp(order.timestamp));
    if (idx === null) continue;
    buckets[idx].sales += order.amount;
    buckets[idx].tips += order.tip;
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const idx = bucketIndexFor(slotStart);
      if (idx !== null) {
        buckets[idx].personnelCost += report.hourlyRate * fractionHours;
      }
    });
  }

  return buckets;
};

/**
 * One bucket per calendar day in the month.
 * Orders and time-report slots are attributed to their *business date* so that
 * overnight orders (e.g. 01:30 on the 1st that belong to the 31st's window)
 * land in the correct day bucket instead of the raw calendar day.
 */
const buildMonthBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): ChartBucket[] => {
  const daysInMonth = selectedDate.lengthOfMonth();
  const year = selectedDate.year();
  const month = selectedDate.monthValue();

  const buckets: ChartBucket[] = Array.from(
    { length: daysInMonth },
    (_, i) => ({
      label: String(i + 1).padStart(2, "0"),
      sales: 0,
      customSales: 0,
      tips: 0,
      personnelCost: 0,
      otherCosts: 0,
      result: 0,
    }),
  );

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const bizDate = getBusinessDate(d, operatingHours);
    if (!bizDate || bizDate.year() !== year || bizDate.monthValue() !== month)
      continue;
    const idx = bizDate.dayOfMonth() - 1;
    if (idx >= 0 && idx < daysInMonth) {
      buckets[idx].sales += order.amount;
      buckets[idx].tips += order.tip;
    }
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const bizDate = getBusinessDate(slotStart, operatingHours);
      if (!bizDate || bizDate.year() !== year || bizDate.monthValue() !== month)
        return;
      const idx = bizDate.dayOfMonth() - 1;
      if (idx >= 0 && idx < daysInMonth) {
        buckets[idx].personnelCost += report.hourlyRate * fractionHours;
      }
    });
  }

  return buckets;
};

/**
 * One bucket per month of the year.
 * Orders and time-report slots are attributed to their *business date's month*
 * so that overnight orders spanning a month boundary land in the right bucket.
 */
const buildYearBuckets = (
  orders: Order[],
  timeReports: TimeReportDto[],
  selectedDate: LocalDate,
  monthLabels: string[],
  operatingHours: OperatingHours,
): ChartBucket[] => {
  const year = selectedDate.year();

  const buckets: ChartBucket[] = Array.from({ length: 12 }, (_, i) => ({
    label: monthLabels[i],
    sales: 0,
    customSales: 0,
    tips: 0,
    personnelCost: 0,
    otherCosts: 0,
    result: 0,
  }));

  for (const order of orders) {
    const d = parseTimestamp(order.timestamp);
    const bizDate = getBusinessDate(d, operatingHours);
    if (!bizDate || bizDate.year() !== year) continue;
    buckets[bizDate.monthValue() - 1].sales += order.amount;
    buckets[bizDate.monthValue() - 1].tips += order.tip;
  }

  for (const report of timeReports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const bizDate = getBusinessDate(slotStart, operatingHours);
      if (!bizDate || bizDate.year() !== year) return;
      buckets[bizDate.monthValue() - 1].personnelCost +=
        report.hourlyRate * fractionHours;
    });
  }

  return buckets;
};

// ─── Date-keyed attribution ───────────────────────────────────────────────────

/** An amount tied to a specific calendar day, e.g. a custom sale or a tip. */
interface DatedAmount {
  /** ISO "YYYY-MM-DD" */
  date: string;
  amount: number;
}

/**
 * Drops date-keyed amounts into the bucket covering their date.
 *
 * Day view is the odd one out: its buckets are hours, so a whole-day amount
 * has no single bucket to land on and is spread evenly across them — the same
 * treatment recurring period costs get.
 */
const attributeByDate = (
  buckets: ChartBucket[],
  entries: DatedAmount[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  apply: (bucket: ChartBucket, amount: number) => void,
): void => {
  if (period === "day") {
    const dayTotal = entries
      .filter((e) => e.date === selectedDate.toString())
      .reduce((sum, e) => sum + e.amount, 0);
    if (dayTotal > 0) {
      const perBucket = dayTotal / buckets.length;
      buckets.forEach((b) => apply(b, perBucket));
    }
    return;
  }

  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(selectedDate);
    for (const entry of entries) {
      const d = LocalDate.parse(entry.date);
      if (!d.isBefore(weekStart) && !d.isAfter(weekEnd)) {
        apply(buckets[d.dayOfWeek().value() - 1], entry.amount);
      }
    }
    return;
  }

  if (period === "month") {
    for (const entry of entries) {
      const d = LocalDate.parse(entry.date);
      if (
        d.year() === selectedDate.year() &&
        d.monthValue() === selectedDate.monthValue()
      ) {
        apply(buckets[d.dayOfMonth() - 1], entry.amount);
      }
    }
    return;
  }

  // year
  for (const entry of entries) {
    const d = LocalDate.parse(entry.date);
    if (d.year() === selectedDate.year()) {
      apply(buckets[d.monthValue() - 1], entry.amount);
    }
  }
};

// ─── Main entry point ─────────────────────────────────────────────────────────

export const buildChartData = (params: BuildChartDataParams): ChartBucket[] => {
  const {
    orders,
    timeReports,
    period,
    selectedDate,
    operatingHours,
    weekdayLabels,
    monthLabels,
    totalPersonnelCost,
    customCosts,
    customSales,
    companyTips,
  } = params;

  const buckets =
    period === "week"
      ? buildWeekBuckets(
          orders,
          timeReports,
          selectedDate,
          weekdayLabels,
          operatingHours,
        )
      : period === "month"
        ? buildMonthBuckets(orders, timeReports, selectedDate, operatingHours)
        : period === "year"
          ? buildYearBuckets(
              orders,
              timeReports,
              selectedDate,
              monthLabels,
              operatingHours,
            )
          : buildDayBuckets(orders, timeReports, selectedDate, operatingHours);

  if (buckets.length === 0) return buckets;

  // Scale raw personnelCost (hourlyRate × hours) so the bucket sum equals
  // the stat card value exactly, regardless of how the API computes total cost.
  if (totalPersonnelCost !== null) {
    const rawTotal = buckets.reduce((sum, b) => sum + b.personnelCost, 0);
    if (rawTotal > 0) {
      const scale = totalPersonnelCost / rawTotal;
      for (const bucket of buckets) {
        bucket.personnelCost *= scale;
      }
    }
  }

  // ── Custom sales: attribute each sale to its specific bucket's own field ────
  // Kept separate from bucket.sales (API sales) so the chart can render them
  // as a distinct series; both still flow into result via the calculation below.
  attributeByDate(buckets, customSales, period, selectedDate, (b, amount) => {
    b.customSales += amount;
  });

  // ── Company tips: tips recorded on the Tips page, folded into the same
  // series as the tips that arrive on POS orders. ─────────────────────────────
  attributeByDate(buckets, companyTips, period, selectedDate, (b, amount) => {
    b.tips += amount;
  });

  // ── Other costs: attribute each cost to its own field ───────────────────────
  // Kept separate from bucket.personnelCost (like customSales is from sales)
  // so the chart can render them as a distinct series; both still flow into
  // result via the calculation below.
  // Period costs recur, so they have no single date to land on — spread them
  // evenly across the period instead.
  const periodCostsTotal = getCustomCostsTotal(
    customCosts.filter((c) => (c.kind ?? "period") === "period"),
    period,
    selectedDate,
  );
  if (periodCostsTotal > 0) {
    const costPerBucket = periodCostsTotal / buckets.length;
    buckets.forEach((b) => {
      b.otherCosts += costPerBucket;
    });
  }

  // Date costs land on their specific bucket.
  attributeByDate(
    buckets,
    customCosts
      .filter((c) => c.kind === "date" && c.date)
      .map((c) => ({ date: c.date!, amount: c.amount })),
    period,
    selectedDate,
    (b, amount) => {
      b.otherCosts += amount;
    },
  );

  // Final result: sales (API + custom) minus personnel cost minus other costs.
  for (const bucket of buckets) {
    bucket.result =
      bucket.sales +
      bucket.customSales -
      bucket.personnelCost -
      bucket.otherCosts;
  }

  return buckets;
};
